import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { 
  UserIcon, 
  AcademicCapIcon, 
  DocumentTextIcon,
  CloudArrowUpIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';
import VerificationInput from '../../components/VerificationInput';

const TeacherRegister = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationType, setVerificationType] = useState<'email' | 'phone' | null>(null);
  const [pendingSubmission, setPendingSubmission] = useState(false);
  
  const [formData, setFormData] = useState({
    // 基本信息
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    idCard: '',
    address: '',
    emergencyContact: '',
    
    // 学历信息
    highestDegree: '',
    graduatedUniversity: '',
    graduationYear: '',
    major: '',
    
    // 教学信息
    title: '',
    introduction: '',
    specialties: '',
    bio: '',
    experience: '',
    teachingExperience: '',
    teachingCases: '',
    certificates: '',
    applicationNotes: ''
  });

  const [files, setFiles] = useState({
    resume: null as File | null,
    portfolio: [] as File[],
    certificates: [] as File[]
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const steps = [
    { id: 1, name: '基本信息', icon: UserIcon },
    { id: 2, name: '学历信息', icon: AcademicCapIcon },
    { id: 3, name: '教学信息', icon: DocumentTextIcon },
    { id: 4, name: '文件上传', icon: CloudArrowUpIcon },
    { id: 5, name: '确认提交', icon: CheckCircleIcon }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // 清除错误
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'resume' | 'portfolio' | 'certificates') => {
    const selectedFiles = Array.from(e.target.files || []);
    
    if (type === 'resume') {
      setFiles(prev => ({ ...prev, resume: selectedFiles[0] || null }));
    } else {
      setFiles(prev => ({ ...prev, [type]: selectedFiles }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.email) newErrors.email = '邮箱是必填项';
        if (!formData.password) newErrors.password = '密码是必填项';
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = '密码确认不匹配';
        }
        if (!formData.firstName) newErrors.firstName = '姓名是必填项';
        if (!formData.lastName) newErrors.lastName = '姓名是必填项';
        if (!formData.phoneNumber) newErrors.phoneNumber = '手机号是必填项';
        break;
      
      case 2:
        if (!formData.highestDegree) newErrors.highestDegree = '最高学历是必填项';
        if (!formData.graduatedUniversity) newErrors.graduatedUniversity = '毕业院校是必填项';
        if (!formData.graduationYear) newErrors.graduationYear = '毕业年份是必填项';
        if (!formData.major) newErrors.major = '专业是必填项';
        break;
      
      case 3:
        if (!formData.title) newErrors.title = '职业头衔是必填项';
        if (!formData.introduction) newErrors.introduction = '个人简介是必填项';
        if (!formData.teachingExperience) newErrors.teachingExperience = '教学经验是必填项';
        break;
      
      case 4:
        if (!files.resume) newErrors.resume = '请上传个人简历';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    // 确定验证方式（优先手机验证）
    if (formData.phoneNumber) {
      setVerificationType('phone');
    } else if (formData.email) {
      setVerificationType('email');
    } else {
      alert('请填写手机号或邮箱以便验证身份');
      return;
    }

    setPendingSubmission(true);
    setShowVerification(true);
  };

  const performSubmission = async () => {
    setLoading(true);
    try {
      const submitData = new FormData();
      
      // 添加表单数据
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      // 添加文件
      if (files.resume) {
        submitData.append('resume', files.resume);
      }
      files.portfolio.forEach(file => {
        submitData.append('portfolio', file);
      });
      files.certificates.forEach(file => {
        submitData.append('certificates', file);
      });

      const response = await fetch('/api/teachers/apply', {
        method: 'POST',
        body: submitData
      });

      const result = await response.json();

      if (response.ok) {
        const isReapplication = result.data?.isReapplication;
        if (isAuthenticated || isReapplication) {
          alert('申请提交成功！您的申请已重新提交，请等待管理员审核。');
          navigate('/teacher/application-status');
        } else {
          alert('申请提交成功！请等待管理员审核。您可以使用注册的邮箱和密码登录查看审核状态。');
          navigate('/login');
        }
      } else {
        alert(result.message || '申请提交失败');
      }
    } catch (error) {
      console.error('申请提交失败:', error);
      alert('申请提交失败，请重试');
    } finally {
      setLoading(false);
      setShowVerification(false);
      setPendingSubmission(false);
    }
  };

  const handleVerificationSuccess = () => {
    performSubmission();
  };

  const handleVerificationCancel = () => {
    setShowVerification(false);
    setVerificationType(null);
    setPendingSubmission(false);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">基本信息</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">邮箱 *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">手机号 *</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">姓 *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">名 *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">密码 *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">确认密码 *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">身份证号</label>
                <input
                  type="text"
                  name="idCard"
                  value={formData.idCard}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">现居地址</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">紧急联系人</label>
                <input
                  type="text"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleInputChange}
                  placeholder="姓名 - 关系 - 电话"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">学历信息</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">最高学历 *</label>
                <select
                  name="highestDegree"
                  value={formData.highestDegree}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">请选择</option>
                  <option value="大专">大专</option>
                  <option value="本科">本科</option>
                  <option value="硕士">硕士</option>
                  <option value="博士">博士</option>
                  <option value="其他">其他</option>
                </select>
                {errors.highestDegree && <p className="text-red-500 text-sm mt-1">{errors.highestDegree}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">毕业院校 *</label>
                <input
                  type="text"
                  name="graduatedUniversity"
                  value={formData.graduatedUniversity}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.graduatedUniversity && <p className="text-red-500 text-sm mt-1">{errors.graduatedUniversity}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">毕业年份 *</label>
                <input
                  type="number"
                  name="graduationYear"
                  value={formData.graduationYear}
                  onChange={handleInputChange}
                  min="1980"
                  max={new Date().getFullYear()}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.graduationYear && <p className="text-red-500 text-sm mt-1">{errors.graduationYear}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">专业 *</label>
                <input
                  type="text"
                  name="major"
                  value={formData.major}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.major && <p className="text-red-500 text-sm mt-1">{errors.major}</p>}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">教学信息</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">职业头衔 *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="如：资深数学教师、英语专业讲师等"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">个人简介 *</label>
                <textarea
                  name="introduction"
                  value={formData.introduction}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="简要介绍您的教学理念和特色"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.introduction && <p className="text-red-500 text-sm mt-1">{errors.introduction}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">专业领域</label>
                <input
                  type="text"
                  name="specialties"
                  value={formData.specialties}
                  onChange={handleInputChange}
                  placeholder="用逗号分隔，如：数学,物理,编程"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">个人履历</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="详细介绍您的教育背景和工作经历"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">教学经验详述 *</label>
                <textarea
                  name="teachingExperience"
                  value={formData.teachingExperience}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="详细描述您的教学经验，包括教学年限、主要成就等"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.teachingExperience && <p className="text-red-500 text-sm mt-1">{errors.teachingExperience}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">教学案例</label>
                <textarea
                  name="teachingCases"
                  value={formData.teachingCases}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="分享一些成功的教学案例或学生反馈"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">获得证书</label>
                <input
                  type="text"
                  name="certificates"
                  value={formData.certificates}
                  onChange={handleInputChange}
                  placeholder="用逗号分隔，如：教师资格证,普通话二甲,TOEFL认证"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">文件上传</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">个人简历 * (PDF/DOC)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange(e, 'resume')}
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {files.resume && <p className="text-green-600 text-sm mt-1">已选择: {files.resume.name}</p>}
                {errors.resume && <p className="text-red-500 text-sm mt-1">{errors.resume}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">作品集/课件样本 (图片/PDF，最多5个)</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  multiple
                  onChange={(e) => handleFileChange(e, 'portfolio')}
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {files.portfolio.length > 0 && (
                  <div className="mt-2">
                    <p className="text-green-600 text-sm">已选择 {files.portfolio.length} 个文件:</p>
                    <ul className="text-sm text-gray-600 ml-4">
                      {files.portfolio.map((file, index) => (
                        <li key={index}>• {file.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">相关证书/资质 (图片/PDF，最多10个)</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={(e) => handleFileChange(e, 'certificates')}
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {files.certificates.length > 0 && (
                  <div className="mt-2">
                    <p className="text-green-600 text-sm">已选择 {files.certificates.length} 个文件:</p>
                    <ul className="text-sm text-gray-600 ml-4">
                      {files.certificates.map((file, index) => (
                        <li key={index}>• {file.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">申请说明与提交</h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">申请说明</label>
                <textarea
                  name="applicationNotes"
                  value={formData.applicationNotes}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="如有其他需要说明的情况，请在此填写"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-md">
                <h4 className="font-medium text-blue-900 mb-2">提交须知</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• 提交申请后，您将收到确认邮件</li>
                  <li>• 管理员将在3-5个工作日内完成审核</li>
                  <li>• 审核期间，您可以登录查看申请状态</li>
                  <li>• 审核通过后，您将获得完整的教师权限</li>
                  <li>• 如有疑问，请联系管理员</li>
                </ul>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 py-12 px-4 sm:px-6 lg:px-12 overflow-hidden">
      {showVerification && verificationType && (
        <VerificationInput
          type={verificationType}
          identifier={verificationType === 'phone' ? formData.phoneNumber : formData.email}
          purpose="register"
          onVerifySuccess={handleVerificationSuccess}
          onCancel={handleVerificationCancel}
        />
      )}

      <div className="absolute inset-0">
        <div className="absolute -top-10 left-20 w-72 h-72 bg-blue-600/30 blur-[180px]" />
        <div className="absolute top-1/3 right-12 w-80 h-80 bg-purple-600/30 blur-[200px]" />
        <div className="absolute bottom-0 inset-x-0 mx-auto w-96 h-96 bg-cyan-500/25 blur-[220px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto space-y-10">
        <div className="text-center text-white">
          <p className="text-sm uppercase tracking-[0.4em] text-blue-200 mb-4">join as mentor</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">成为 Philiapedia 全球精英导师</h1>
          <p className="text-blue-100 max-w-3xl mx-auto">
            我们正在招募拥有国际视野、热爱教学的导师。帮助学生完成科研、语言、申请等关键节点，同时获得专属 AI 助手、内容生产工具和全球教学网络。
          </p>
        </div>

        <div className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/30">
          {isAuthenticated && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mx-8 mt-8 rounded-2xl text-blue-800">
              <p className="font-semibold">重新申请</p>
              <p className="text-sm">我们已记录您之前的资料，请根据提示更新信息，提交后 3-5 个工作日即可收到最新审核结果。</p>
            </div>
          )}

          <div className="px-6 md:px-10 py-8">
            <div className="flex flex-col lg:flex-row lg:items-start gap-8">
              <div className="lg:w-1/4">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-4">申请进度</p>
                <nav className="space-y-4">
                  {steps.map(step => (
                    <div key={step.id} className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                        step.id < currentStep
                          ? 'bg-green-500/10 border-green-500 text-green-600'
                          : step.id === currentStep
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-200 text-gray-400'
                      }`}>
                        <step.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{step.name}</p>
                        <p className="text-xs text-gray-500">
                          {step.id < currentStep
                            ? '已完成'
                            : step.id === currentStep
                              ? '进行中'
                              : '待填写'}
                        </p>
                      </div>
                    </div>
                  ))}
                </nav>
              </div>

              <div className="lg:w-3/4 bg-white rounded-2xl border border-gray-100 shadow-inner p-6">
                {renderStepContent()}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 px-6 md:px-10 py-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <button
              onClick={handlePrev}
              disabled={currentStep === 1}
              className={`px-5 py-3 rounded-xl text-sm font-medium transition-all ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-400 shadow-sm'
              }`}
            >
              上一步
            </button>

            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-4 w-full md:w-auto">
              {!isAuthenticated && (
                <button
                  onClick={() => navigate('/login')}
                  className="px-5 py-3 rounded-xl text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
                >
                  已有账号？去登录
                </button>
              )}

              {currentStep < 5 ? (
                <button
                  onClick={handleNext}
                  className="px-6 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-600/30"
                >
                  下一步
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`px-6 py-3 rounded-xl text-sm font-semibold text-white shadow-lg ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-green-500/30'
                  }`}
                >
                  {loading ? '提交中...' : '提交申请'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherRegister;